import React, { useState, useEffect } from 'react';
import { EstimateProjectCustomer } from '../estimate/estimateType';
import EstimateApi from './estimateApi';
import { useNavigate } from 'react-router-dom';
import EstimatesTableView from './estimateTableView';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import Spinner from '../components/ui/spinner';
import { FilePlus, PencilRuler } from 'lucide-react';

const EstimatePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/Estimate' },
  ];

  const [estimates, setEstimates] = useState<EstimateProjectCustomer[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [filterCustomer, setFilterCustomer] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEstimates = async () => {
      try {
        setLoading(true);
        const data = await EstimateApi.getEstimatesProjectsCustomers();
        setEstimates(data);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchEstimates();
  }, []);

  const handleEdit = (estimateId: string) => {
    const selectedEstimate = estimates.find(estimate => estimate.estimate_id === estimateId);
    if (selectedEstimate) {
      navigate('/main/project/estimate/update', { state: { estimate: selectedEstimate } });
    } else {
      setErrorMessage("Error: update estimate");
    }
  };

  const handleDelete = async (estimateId: string) => {
    const selectedEstimate = estimates.find(estimate => estimate.estimate_id === estimateId);
    if (selectedEstimate) {
      navigate('/main/project/estimate/delete', { state: { estimate: selectedEstimate } });
    } else {
      setErrorMessage("Error: update estimate");
    }
  };

  const handleSendToCustomer = async (estimateId: string) => {
    const selectedEstimate = estimates.find(estimate => estimate.estimate_id === estimateId);
    if (selectedEstimate) {
      // console.log('Selected estimate for sending to customer:', selectedEstimate);
      navigate('/main/project/estimate/tocustomer', { state: { estimate: selectedEstimate } });
    } else {
      setErrorMessage("Error: Estimate not found");
    }
  };

  const handleText = (estimate: EstimateProjectCustomer, estimateId: string) => {
    //alert('A text will be sent to the customer with the estimate: TBD');
    //window.location.href = `sms:${estimate.customer_phone}`;
  };

  const handleCreateEstimate = () => {
    navigate('/main/project/estimate/create');
  };

  const handleView = async (estimateId: string) => {
    setErrorMessage('');

    const selectedEstimate = estimates.find(estimate => estimate.estimate_id === estimateId);
    if (selectedEstimate) {
      try {
        const pdfBlob = await EstimateApi.viewEstimatePdf(estimateId);
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    } else {
      setErrorMessage("Error: estimate not found");
    }
  };

  // Get unique projects and customers for filters - from ALL estimates
  const uniqueProjects = React.useMemo(() => {
    const projects = Array.from(new Set(estimates.map(e => e.project_name)))
      .filter(name => name && name.trim() !== '')
      .sort();
    //console.log('Total estimates:', estimates.length);
    //console.log('Unique projects:', projects);
    return projects;
  }, [estimates]);

  const uniqueCustomers = React.useMemo(() => {
    const customers = Array.from(new Set(estimates.map(e => `${e.customer_first_name} ${e.customer_last_name}`)))
      .filter(name => name && name.trim() !== '')
      .sort();
    //console.log('Unique customers:', customers);
    return customers;
  }, [estimates]);

  // Filter estimates based on search and filters
  const filteredEstimates = estimates.filter(estimate => {
    const matchesSearch = searchTerm === '' ||
      estimate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      estimate.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      estimate.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${estimate.customer_first_name} ${estimate.customer_last_name}`.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesProject = filterProject === 'all' || estimate.project_name === filterProject;
    const matchesCustomer = filterCustomer === 'all' ||
      `${estimate.customer_first_name} ${estimate.customer_last_name}` === filterCustomer;

    // Debug logging
    if (filterProject !== 'all' || filterCustomer !== 'all') {
      console.log('Filtering estimate:', {
        estimateName: estimate.name,
        projectName: estimate.project_name,
        filterProject: filterProject,
        matchesProject: matchesProject,
        customerName: `${estimate.customer_first_name} ${estimate.customer_last_name}`,
        filterCustomer: filterCustomer,
        matchesCustomer: matchesCustomer,
        finalResult: matchesSearch && matchesProject && matchesCustomer
      });
    }

    return matchesSearch && matchesProject && matchesCustomer;
  });

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          {/* Header */}


          <div className="mb-4">



<div className="mb-4">
  <div className="flex items-center justify-between mb-2">
    <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
     
      Estimate List
    </h1>
    <Button size="medium" variant="default" onClick={handleCreateEstimate}>
      <FilePlus size={18} />
      Create Estimate
    </Button>
  </div>
  <p className="text-sm text-paragraph">
      Select radio button to edit estimate, view estimate in PDF or send estimate to customer. Click the down arrow to view itemized estimates.

  </p>
</div>


            {/** 
            <Button
              size="large"
              variant="default"
              onClick={handleCreateEstimate}
              className="shrink-0"
            >
              <FilePlus size={20} />
              Create Estimate
            </Button>
*/}
          </div>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message */}
          {message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          {/* Search and Filter Bar */}
          {!loading && estimates.length > 0 && (
            <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Input
                    type="text"
                    placeholder="Search estimates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div>
                  <Select
                    value={filterProject}
                    onChange={(e) => setFilterProject(e.target.value)}
                  >
                    <option value="all">All Projects</option>
                    {uniqueProjects.map((project) => (
                      <option key={project} value={project}>
                        {project}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Select
                    value={filterCustomer}
                    onChange={(e) => setFilterCustomer(e.target.value)}
                  >
                    <option value="all">All Customers</option>
                    {uniqueCustomers.map((customer) => (
                      <option key={customer} value={customer}>
                        {customer}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              {filteredEstimates.length !== estimates.length && (
                <div className="mt-3 text-sm text-gray-600">
                  Showing {filteredEstimates.length} of {estimates.length} estimates
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner />
            </div>
          ) : estimates.length > 0 ? (
            filteredEstimates.length > 0 ? (
              <EstimatesTableView
                estimates={filteredEstimates}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendToCustomer={handleSendToCustomer}
                onText={handleText}
                onView={handleView}
              />
            ) : (
              <div className="text-center py-12">
                <Alert variant="info">No estimates match your search or filter criteria.</Alert>
              </div>
            )
          ) : (
            <div className="text-center py-12">
              <Alert variant="info">No estimates found. Click the "Create Estimate" button above to get started.</Alert>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EstimatePage;